import React, { useCallback, useEffect, useMemo, useState } from "react";

import { LayoutList } from "lucide-react";

import BriefingPreview from "./BriefingPreview";
import { ghostButton, Pill } from "./parts";
import PublishPanel from "./PublishPanel";
import QuestionsPanel from "./QuestionsPanel";
import SectionsPanel from "./SectionsPanel";
import { S } from "./strings";
import { isCustomisedFromDefault, same, templateChanges } from "./templateDiff";
import Button from "../../Components/Button";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  DEFAULT_SCOPE,
  discardTemplateDraftOnServer,
  fetchAccountPlanTemplate,
  fetchTemplateScope,
  PlanTemplate,
  revertTemplateEdits,
  saveTemplateDraft,
  savedTemplate,
  setTemplateScope,
  TemplateTenant,
} from "../../Store/Reducers/AccountPlanTemplateSlice";

type TabId = "questions" | "sections" | "publish";

const countCustomised = (defaults: PlanTemplate, t: PlanTemplate): number =>
  t.questions.filter(
    q =>
      !defaults.questions.some(d => d.key === q.key) ||
      isCustomisedFromDefault(defaults, "questions", q)
  ).length +
  t.sections.filter(
    s =>
      !defaults.sections.some(d => d.key === s.key) ||
      isCustomisedFromDefault(defaults, "sections", s)
  ).length;

const formatWhen = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : "";

const tenantLabel = (t: TemplateTenant): string =>
  [
    t.name,
    t.follows_default ? S.tenantFollows : S.tenantCustomised(t.customised),
    t.has_draft ? S.scopeDraftMark : "",
  ]
    .filter(Boolean)
    .join(" · ");

const AdminAccountPlansTemplateContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    overview,
    loading,
    loadError,
    scope,
    details,
    working,
    busy,
    actionError,
  } = useAppSelector(state => state.accountPlanTemplate);
  const [tab, setTab] = useState<TabId>("questions");

  const load = useCallback(() => {
    void dispatch(fetchAccountPlanTemplate());
  }, [dispatch]);

  useEffect(() => {
    if (!overview) load();
  }, [overview, load]);

  const detail = details[scope];
  useEffect(() => {
    if (!detail) void dispatch(fetchTemplateScope(scope));
  }, [detail, scope, dispatch]);

  // Saved drafts, publishes and rollbacks change what the tenant list shows.
  const refreshOverview = useCallback(() => {
    void dispatch(fetchAccountPlanTemplate());
  }, [dispatch]);
  useEffect(() => {
    if (detail) refreshOverview();
  }, [detail, refreshOverview]);

  const isDefaultScope = scope === DEFAULT_SCOPE;
  const saved = detail ? savedTemplate(detail) : null;
  const template = working[scope] ?? saved;
  const live = detail?.live.template ?? null;
  const dirty = !!working[scope] && !same(working[scope], saved);
  const changes = useMemo(
    () => (live && template ? templateChanges(live, template) : []),
    [live, template]
  );
  const liveKeys = useMemo(
    () =>
      new Set(
        live ? [...live.questions, ...live.sections].map(x => x.key) : []
      ),
    [live]
  );

  if (loadError) {
    return (
      <div className="p-4 md:p-6">
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          <p>{loadError}</p>
          <Button variant="outline-primary" onClick={load}>
            {S.retry}
          </Button>
        </div>
      </div>
    );
  }

  if (!overview || !detail || !template || !live) {
    return (
      <div className="p-4 text-sm text-gray-500 md:p-6">
        {loading || !detail ? S.loading : ""}
      </div>
    );
  }

  const defaults = detail.default_template;
  const target = isDefaultScope ? S.theDefault : (detail.name ?? scope);
  const customised = isDefaultScope ? 0 : countCustomised(defaults, template);
  const version = detail.live.version;

  const liveLabel = detail.live.follows_default
    ? S.followsDefaultPill(version?.version_no ?? null)
    : version
      ? S.liveVersion(version.version_no, version.by)
      : S.liveCode;

  const tabs: { id: TabId; label: string; count: number | null }[] = [
    {
      id: "questions",
      label: S.tabQuestions,
      count: template.questions.filter(q => q.enabled).length,
    },
    {
      id: "sections",
      label: S.tabSections,
      count: template.sections.filter(s => s.enabled).length,
    },
    { id: "publish", label: S.tabPublish, count: changes.length || null },
  ];

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <LayoutList size={20} />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">{S.title}</h2>
            <p className="mt-1 max-w-2xl text-sm text-gray-600">{S.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="tpl-scope" className="text-sm text-gray-600">
            {S.editing}
          </label>
          <select
            id="tpl-scope"
            value={scope}
            onChange={e => dispatch(setTemplateScope(e.target.value))}
            className="max-w-[300px] rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
          >
            <option value={DEFAULT_SCOPE}>
              {S.defaultScope}
              {overview.default.has_draft ? ` · ${S.scopeDraftMark}` : ""}
            </option>
            {overview.tenants.map(t => (
              <option key={t.schema} value={t.schema}>
                {tenantLabel(t)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-600">
        <Pill tone="green">{liveLabel}</Pill>
        {!isDefaultScope && (
          <Pill tone={customised ? "blue" : "gray"}>
            {customised ? S.customisedCount(customised) : S.sameAsDefault}
          </Pill>
        )}
        <span
          className={changes.length ? "font-semibold text-gray-900" : undefined}
        >
          {S.unpublished(changes.length)}
        </span>
        {dirty ? (
          <span className="text-amber-700">{S.unsavedEdits}</span>
        ) : (
          detail.draft && (
            <span className="text-gray-500">
              {S.draftSaved(detail.draft.by, formatWhen(detail.draft.at))}
            </span>
          )
        )}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {dirty && (
            <button
              type="button"
              className={ghostButton}
              onClick={() => dispatch(revertTemplateEdits())}
            >
              {S.revertEdits}
            </button>
          )}
          {!dirty && detail.draft && (
            <button
              type="button"
              className={ghostButton}
              disabled={busy !== null}
              onClick={() => void dispatch(discardTemplateDraftOnServer(scope))}
            >
              {S.discardDraft}
            </button>
          )}
          {dirty && (
            <Button
              variant="outline-primary"
              loading={busy === "save"}
              disabled={busy !== null}
              onClick={() => void dispatch(saveTemplateDraft(scope))}
            >
              {S.saveDraft}
            </Button>
          )}
          {changes.length > 0 && tab !== "publish" && (
            <Button variant="primary" onClick={() => setTab("publish")}>
              {S.reviewPublish}
            </Button>
          )}
        </span>
      </div>

      {actionError && (
        <p
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {actionError}
        </p>
      )}

      <div
        role="tablist"
        className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200"
      >
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.id
                ? "border-blue-600 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
            {t.count !== null && (
              <span className="ml-1.5 text-xs text-gray-400">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          {tab === "questions" && (
            <QuestionsPanel
              key={scope}
              template={template}
              defaults={defaults}
              isDefaultScope={isDefaultScope}
              liveKeys={liveKeys}
            />
          )}
          {tab === "sections" && (
            <SectionsPanel
              key={scope}
              template={template}
              defaults={defaults}
              isDefaultScope={isDefaultScope}
              liveKeys={liveKeys}
              owners={overview.owners}
            />
          )}
          {tab === "publish" && (
            <PublishPanel
              key={scope}
              scope={scope}
              detail={detail}
              changes={changes}
              isDefaultScope={isDefaultScope}
              target={target}
              busy={busy}
            />
          )}
        </div>
        <BriefingPreview template={template} />
      </div>
    </div>
  );
};

export default AdminAccountPlansTemplateContainer;
